// @ts-nocheck
/*
 * Port of voice-library/manage-prompts.js (744 lines). BYTE-IDENTICAL.
 * Prompt-approval workspace. The main list is roughly the same as
 * View-voice-files, but the per-language file drill-down modal here adds
 * Approve/Reject buttons per language row (backend endpoint
 * `/v2/prompt/:id/language-status`). Bulk approve/reject of the whole
 * prompt via `/v2/prompt/:id` (`PROMPT_STATUS.APPROVED|REJECT`).
 * The HangupCausePromptMapping subview is imported but rendered inside a
 * v1 comment block â€” kept commented here too to match shipped v1 UI.
 * PrimeReact DataTable/Checkbox/Dialog â†’ obd3-gui DataTable + tanstack
 * ColumnDef + native checkbox + react-bootstrap Modal (same swap as
 * ViewVoiceFilesPage). LottieAnimation dropped (react-toastify replaces
 * the popup that v1 wrapped around it).
 */
import React, { useState, useEffect } from 'react';
import { storageUrlOr } from '@/utilities/storageUrl';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ fluid, className, children, ...props }: any) => (
  <div className={_cx(fluid ? 'container-fluid' : 'container', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
Card.Text = ({ className, children, ...props }: any) => (
  <p className={_cx('card-text', className)} {...props}>
    {children}
  </p>
);
Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const _TabsCtx = React.createContext<any>({});
const Tabs: any = ({
  activeKey,
  defaultActiveKey,
  onSelect,
  children,
  className,
  ...props
}: any) => {
  const [internalKey, setInternalKey] = React.useState(defaultActiveKey || null);
  const current = activeKey !== undefined ? activeKey : internalKey;
  const handle = (k: any) => {
    if (activeKey === undefined) setInternalKey(k);
    onSelect?.(k);
  };
  const tabs = React.Children.toArray(children).filter(Boolean) as any[];
  if (current === null && tabs.length) {
    setInternalKey(tabs[0]?.props?.eventKey);
  }
  return (
    <_TabsCtx.Provider value={{ current, setKey: handle }}>
      <div className={className} {...props}>
        <ul className="nav nav-tabs" role="tablist">
          {tabs.map((t: any) => (
            <li key={t.props.eventKey} className="nav-item" role="presentation">
              <button
                type="button"
                className={_cx('nav-link', current === t.props.eventKey && 'active')}
                onClick={() => handle(t.props.eventKey)}
                disabled={t.props.disabled}
              >
                {t.props.title}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {tabs.map((t: any) => (
            <div
              key={t.props.eventKey}
              className={_cx('tab-pane', current === t.props.eventKey && 'active show')}
              style={{ display: current === t.props.eventKey ? undefined : 'none' }}
            >
              {t.props.children}
            </div>
          ))}
        </div>
      </div>
    </_TabsCtx.Provider>
  );
};
const Tab: any = () => null;
const Tooltip: any = ({ children, id: _id, ...props }: any) => (
  <span {...props}>{children}</span>
);
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { getApiCall, postApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { API_PROMPT_ACTIVE_LIST } from '@/service/url';
import { PROMPT_STATUS, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import HangupCausePromptMapping from './HangupCausePromptMappingPage';
import './index.css';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as PageButton } from '@/components/ui/button';
import { Download, Trash2, RefreshCw, Eye, Check, X, Play, Square, CheckCircle2, XCircle } from 'lucide-react';
import {
  Tabs as UiTabs,
  TabsContent as UiTabsContent,
  TabsList as UiTabsList,
  TabsTrigger as UiTabsTrigger,
} from '@/components/ui/tabs';
import DeletedFilesTab from './DeletedFilesTab';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const API_BASE = '/v2';

const DeleteIcon = ({ handleCallback }: any) => (
  <Link
    className="btn btn-sm btn-icon btn-danger"
    title="Delete"
    to="#"
    onClick={(e) => {
      e.preventDefault();
      handleCallback();
    }}
  >
    <span className="btn-inner">
      <svg
        width="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        stroke="currentColor"
      >
        <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  </Link>
);

function getFirstUrl(prompt_url: any) {
  if (!prompt_url) return '';
  if (typeof prompt_url === 'string') return prompt_url;
  if (Array.isArray(prompt_url) && prompt_url.length > 0)
    return prompt_url[0]?.url || '';
  return '';
}

function getPromptApprovedAt(rowData: any) {
  const entries = Array.isArray(rowData?.prompt_url) ? rowData.prompt_url : [];
  const approvedTimes = entries
    .map((e: any) => e?.approved_at)
    .filter(Boolean)
    .map((d: any) => new Date(d).getTime())
    .filter((t: number) => !Number.isNaN(t));
  if (approvedTimes.length > 0) return new Date(Math.max(...approvedTimes));
  return rowData?.approval_time ? new Date(rowData.approval_time) : null;
}

const ManagePrompts = () => {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(false);
  const [filesModal, setFilesModal] = useState<any>({
    visible: false,
    entries: [],
    promptName: '',
    promptId: null,
    promptStatus: '',
  });
  const [playingUrl, setPlayingUrl] = useState('');
  const [selectedData, setSelectedPrompt] = useState<any[]>([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [deletePromptId, setDeletePromptId] = useState<any>(undefined);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<any>(null);
  const [originalDataList, setOriginalDataList] = useState<any[]>([]);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [activeTab, setActiveTab] = useState('prompts');

  useEffect(() => {
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    fetchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger]);

  const fetchPrompts = () => {
    setLoading(true);
    getApiCall(`${API_PROMPT_ACTIVE_LIST}`)
      .then(onPromptsSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  };

  const showToasts = (message: string) => {
    toast.success(message, {
      style: { background: '#16a34a', color: '#fff' },
      progressStyle: { background: '#bbf7d0' },
      iconTheme: { primary: '#fff', secondary: '#16a34a' },
    } as any);
  };

  useEffect(() => {
    if (searchValue && searchValue.trim() !== '') {
      const filtered = originalDataList.filter((data: any) =>
        data.prompt_name.toLowerCase().includes(searchValue.trim().toLowerCase()),
      );
      setPrompts(filtered);
    } else {
      setPrompts(originalDataList);
    }
  }, [searchValue, originalDataList]);

  const onPromptsSuccess = (resp: any) => {
    const sortedResp = [...resp].sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const updatedList = sortedResp.map((item: any, index: number) => ({
      ...item,
      key: index + 1,
      id: item.id,
      status:
        item.prompt_status == 0
          ? 'Pending'
          : item.prompt_status == 1
          ? 'Approved'
          : 'Rejected',
    }));
    setPrompts(updatedList);
    setOriginalDataList(updatedList);
  };

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleLanguageStatus = (promptId: any, language: string, status: any) => {
    postApiCall(`${API_BASE}/prompt/${promptId}/language-status`, {
      language,
      status,
    })
      .then((res: any) => {
        const isApproved = status === PROMPT_STATUS.APPROVED;
        const updatedEntry = res?.entry || null;
        showToasts(
          isApproved
            ? `Voice file (${language.toUpperCase()}) has been approved successfully.`
            : `Voice file (${language.toUpperCase()}) has been rejected.`,
        );
        const mergeEntry = (e: any) => {
          if (e.language !== language) return e;
          return updatedEntry ? { ...updatedEntry } : { ...e, status };
        };
        setPrompts((prev: any[]) =>
          prev.map((p: any) => {
            if (p.id !== promptId) return p;
            const updatedUrl = Array.isArray(p.prompt_url)
              ? p.prompt_url.map(mergeEntry)
              : p.prompt_url;
            return { ...p, prompt_url: updatedUrl };
          }),
        );
        setFilesModal((prev: any) => ({
          ...prev,
          entries: prev.entries.map(mergeEntry),
        }));
      })
      .catch((err: any) => toast.error(friendlyApiError(err)));
  };

  const handleApprove = (id: any) => {
    const payload = { status: PROMPT_STATUS.APPROVED };

    postApiCall(`${API_BASE}/prompt/${id}`, payload)
      .then(() => {
        showToasts('Voice prompt has been approved successfully.');
        setPrompts((prevPrompts: any[]) =>
          prevPrompts.map((prompt: any) =>
            prompt.id === id ? { ...prompt, status: 'Approved' } : prompt,
          ),
        );
        setReloadTrigger((prev) => !prev);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Failed to approve voice prompt');
      });
  };

  const handleReject = (id: any) => {
    const payload = { status: PROMPT_STATUS.REJECT };

    postApiCall(`${API_BASE}/prompt/${id}`, payload)
      .then(() => {
        showToasts('Voice prompt has been rejected.');
        setPrompts((prevPrompts: any[]) =>
          prevPrompts.map((prompt: any) =>
            prompt.id === id ? { ...prompt, status: 'Rejected' } : prompt,
          ),
        );
        setReloadTrigger((prev) => !prev);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Failed to reject voice prompt');
      });
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };

  function handlePromptDelete(id: any) {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeletePromptId(id);
  }

  const [buttonDisable, setButtonDisable] = useState(false);

  const handleSelectAll = () => {
    if (selectedData.length === prompts.length) {
      setSelectedPrompt([]);
    } else {
      const allPromptsIds = prompts.map((prompt: any) => prompt.id);
      setSelectedPrompt(allPromptsIds);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    fetchPrompts();
  };

  useEffect(() => {
    if (confirmDelete) {
      postApiCall(`${API_BASE}/prompt/delete`, {
        promptIds: isMultiDelete ? selectedData : [deletePromptId],
      })
        .then(onDeleteSuccess)
        .catch(onDeleteFail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete]);

  function onDeleteSuccess(resp: any) {
    toast.success(resp);
    let updatedList: any[];
    if (isMultiDelete) {
      updatedList = prompts
        .filter((val: any) => !selectedData.includes(val.id))
        .map((val: any, key: number) => {
          val['key'] = key + 1;
          return val;
        });
      setSelectedPrompt([]);
    } else {
      updatedList = prompts
        .filter((val: any) => val.id != deletePromptId)
        .map((val: any, key: number) => {
          val['key'] = key + 1;
          return val;
        });
    }
    setPrompts(updatedList);
    setEnableDeleteModal(false);
    setDeletePromptId(undefined);
    setConfirmDelete(false);
    fetchPrompts();
  }

  function onDeleteFail(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.warning(friendlyApiError(err));
    }
    setConfirmDelete(false);
  }

  const openFilesModal = (rowData: any) => {
    const raw = Array.isArray(rowData.prompt_url)
      ? rowData.prompt_url
      : rowData.prompt_url
      ? [
          {
            language: 'hi',
            url: rowData.prompt_url,
            duration: rowData.prompt_duration || 0,
            status: rowData.prompt_status || 0,
          },
        ]
      : [];
    const entries = raw.map((entry: any) => ({
      ...entry,
      url: storageUrlOr('prompt', entry?.blob_path, entry?.url),
    }));
    setPlayingUrl('');
    setFilesModal({
      visible: true,
      entries,
      promptName: rowData.prompt_name,
      promptId: rowData.id,
      promptStatus: rowData.status,
    });
  };

  const closeFilesModal = () => {
    setFilesModal({
      visible: false,
      entries: [],
      promptName: '',
      promptId: null,
      promptStatus: '',
    });
    setPlayingUrl('');
  };

  const onError = (error: any) => {
    toast.warning(friendlyApiError(error));
    setShowToast(true);
  };

  const handleCheckboxToggle = (promptId: any) => {
    const isSelected = selectedData.includes(promptId);
    setSelectedPrompt(
      isSelected
        ? selectedData.filter((id: any) => id !== promptId)
        : [...selectedData, promptId],
    );
  };

  const downloadFile = async (fileUrl: string, filename?: string) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch the file');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename || fileUrl.split('/').pop() || 'prompt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error while downloading the file:', error);
    }
  };

  const actionBodyTemplate = (rowData: any) => {
    const isDeleted =
      rowData?.is_deleted === true ||
      Number(rowData?.is_deleted) === 1 ||
      Boolean(rowData?.deleted_at);

    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openFilesModal(rowData);
          }}
          title="View files"
          aria-label="View files"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        {(isSuperAdmin || loggedInUserACL[MODULE_ACL.MANAGE_PROMPTS]?.delete) &&
          !isDeleted && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handlePromptDelete(rowData?.id);
              }}
              title="Delete"
              aria-label="Delete"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
      </div>
    );
  };

  if (loading) return <Loader />;

  const handleExport = async () => {
    try {
      setButtonDisable(true);

      exportToCSV({
        fileName: 'prompts-list',
        data: prompts,
        columns: [
          { header: 'Name', accessor: 'prompt_name' },
          { header: 'Duration', accessor: 'prompt_duration' },
          { header: 'Status', accessor: 'status' },
          {
            header: 'Languages',
            accessor: (row: any) => {
              if (!row.prompt_url) return '-';
              if (typeof row.prompt_url === 'string') return row.prompt_url;
              return row.prompt_url.map((e: any) => e.language || '-').join(', ');
            },
          },
          { header: 'Category', accessor: 'prompt_category' },
          {
            header: 'Created By',
            accessor: (row: any) => row.creator?.name || row.creator?.username || '-',
          },
          {
            header: 'Deleted By',
            accessor: (row: any) => row.deletedBy?.name || row.deletedBy?.username || '-',
          },
          {
            header: 'Deleted At',
            accessor: (row: any) =>
              row.deleted_at
                ? new Date(row.deleted_at).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '-',
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={selectedData.length === prompts.length && prompts.length > 0}
          onChange={handleSelectAll}
        />
      ),
      enableSorting: false,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedData.includes(row.original.id)}
          onChange={() => handleCheckboxToggle(row.original.id)}
        />
      ),
    },
    { accessorKey: 'prompt_name', header: 'Name' },
    { accessorKey: 'prompt_duration', header: 'Total Duration' },
    {
      id: 'creator',
      header: 'Created By',
      cell: ({ row }: any) =>
        row.original?.creator?.name || row.original?.creator?.username || '-',
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }: any) => {
        if (!row.original.created_at) return '-';
        return new Date(row.original.created_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      id: 'approvedAt',
      header: 'Approved At',
      cell: ({ row }: any) => {
        const approvedAt = getPromptApprovedAt(row.original);
        if (!approvedAt) return '-';
        return approvedAt.toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      id: 'deletedBy',
      header: 'Deleted By',
      cell: ({ row }: any) => row.original?.deletedBy?.name || '-',
    },
    {
      accessorKey: 'deleted_at',
      header: 'Deleted At',
      cell: ({ row }: any) => {
        if (!row.original.deleted_at) return '';
        return new Date(row.original.deleted_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      id: 'action',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }: any) => actionBodyTemplate(row.original),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Voice"
        description="Voice prompts uploaded for use in IVR flows and hangup responses."
        actions={
          activeTab !== 'prompts' ? null : (
          <div className="flex flex-wrap items-center gap-2">
            <PageButton
              onClick={handleClear}
              size="sm"
              variant="outline"
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </PageButton>
            {prompts.length > 0 && (
              <PageButton
                onClick={handleExport}
                size="sm"
                variant="outline"
                disabled={buttonDisable}
                className="gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Export Data
                {buttonDisable && (
                  <span
                    className="spinner-border spinner-border-sm ml-2"
                    role="status"
                    aria-hidden="true"
                  />
                )}
              </PageButton>
            )}
            {(isSuperAdmin || loggedInUserACL[MODULE_ACL.MANAGE_PROMPTS]?.delete) && (
              <PageButton
                variant="destructive"
                size="sm"
                onClick={handleMultiDelete}
                disabled={selectedData.length === 0}
                className="gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete Selected
              </PageButton>
            )}
          </div>
          )
        }
      />
      {isSuperAdmin && (
        <UiTabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <UiTabsList>
            <UiTabsTrigger value="prompts">Prompts</UiTabsTrigger>
            <UiTabsTrigger value="deleted">Deleted Files</UiTabsTrigger>
          </UiTabsList>
          <UiTabsContent value="deleted" className="mt-4">
            <DeletedFilesTab />
          </UiTabsContent>
        </UiTabs>
      )}
      <div className="mt-4" style={{ display: activeTab === 'prompts' ? undefined : 'none' }}>
        <div className="scroll-container">
          <DataTable
            tableId="manage-prompts:v2"
            columns={columns}
            data={prompts}
            pageSize={10}
            emptyTitle="No prompts"
            disableExport
          />
        </div>

        {isSuperAdmin && <HangupCausePromptMapping />}

        <RenderDeleteModal
          show={enableDeleteModal}
          setShow={setEnableDeleteModal}
          setConfirmDelete={setConfirmDelete}
          isMultiDelete={isMultiDelete}
          clearSelectedData={() => setSelectedPrompt([])}
        />
      </div>

      <Modal show={filesModal.visible} onHide={closeFilesModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Voice Files — {filesModal.promptName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="overflow-x-auto rounded-lg border border-border/60">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="bg-muted/50 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="whitespace-nowrap px-3 py-2 text-left">Language</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left">File Name</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left">Duration</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left">Status</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left">Action Time</th>
                  <th className="whitespace-nowrap px-3 py-2 text-center">Listen / Download</th>
                  <th className="whitespace-nowrap px-3 py-2 text-center">Approve / Reject</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {filesModal.entries.map((entry: any, i: number) => {
                  const isPlaying = playingUrl === entry.url;
                  const isApproved = entry.status == 1;
                  const isRejected = entry.status == 2;
                  const stTone = isApproved
                    ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                    : isRejected
                    ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
                    : 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300';
                  return (
                    <tr
                      key={i}
                      className={
                        'transition-colors hover:bg-primary/[0.06] ' +
                        (isPlaying ? 'bg-primary/[0.08]' : i % 2 === 1 ? 'bg-muted/30' : 'bg-card')
                      }
                    >
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary ring-1 ring-inset ring-primary/25">
                          {entry.language || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {entry.name || filesModal.promptName || '—'}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-foreground">
                        {entry.duration ? entry.duration + 's' : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                            stTone
                          }
                        >
                          {isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">
                        {entry.approved_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300"
                            title={new Date(entry.approved_at).toLocaleString('en-IN')}
                          >
                            <Check className="h-3.5 w-3.5" />
                            {new Date(entry.approved_at).toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                        ) : entry.rejected_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-300"
                            title={new Date(entry.rejected_at).toLocaleString('en-IN')}
                          >
                            <X className="h-3.5 w-3.5" />
                            {new Date(entry.rejected_at).toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => setPlayingUrl(isPlaying ? '' : entry.url)}
                            title={isPlaying ? 'Stop' : 'Listen'}
                            aria-label={isPlaying ? 'Stop' : 'Listen'}
                            className={
                              'inline-flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset transition-colors ' +
                              (isPlaying
                                ? 'bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
                                : 'bg-primary/10 text-primary ring-primary/20 hover:bg-primary/15')
                            }
                          >
                            {isPlaying ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              downloadFile(
                                entry.url,
                                filesModal.promptName + '_' + entry.language,
                              )
                            }
                            title="Download"
                            aria-label="Download"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            title="Approve"
                            aria-label="Approve"
                            disabled={isApproved}
                            onClick={() =>
                              handleLanguageStatus(filesModal.promptId, entry.language, PROMPT_STATUS.APPROVED)
                            }
                            className={
                              'inline-flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset transition-colors ' +
                              (isApproved
                                ? 'bg-emerald-100 text-emerald-700 opacity-40 cursor-not-allowed ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                                : 'bg-emerald-100 text-emerald-700 ring-emerald-200 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300')
                            }
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Reject"
                            aria-label="Reject"
                            disabled={isRejected}
                            onClick={() =>
                              handleLanguageStatus(filesModal.promptId, entry.language, PROMPT_STATUS.REJECT)
                            }
                            className={
                              'inline-flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset transition-colors ' +
                              (isRejected
                                ? 'bg-rose-100 text-rose-700 opacity-40 cursor-not-allowed ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
                                : 'bg-rose-100 text-rose-700 ring-rose-200 hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
                            }
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {playingUrl && (
            <div className="mt-4 rounded-lg border border-border/60 bg-muted/40 p-3">
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Now Playing
              </div>
              <audio key={playingUrl} controls autoPlay className="w-full">
                <source src={playingUrl} type="audio/mpeg" />
              </audio>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </FillPage>
  );
};

export default ManagePrompts;
